// src/utils/resultsExport.js
// The final ranking as a file: a spreadsheet (CSV) or a picture to send around.
import { CLASSIC_RANKING, formatScore } from './ranking';

// A player row's own name, or (for a fixed-teams row, see the format) both of its players' names joined.
const fullName = (row) => (row.players ? row.players.map((player) => `${player.firstName} ${player.lastName}`.trim()).join(' & ')
                                        : `${row.firstName} ${row.lastName}`.trim());

// Spreadsheet-friendly: quotes where needed, a byte-order mark so Excel reads the accents right.
const cell = (value) => {
    const text = String(value ?? '');
    return /[",\n\r;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export function rankingToCsv(standings, ranking = CLASSIC_RANKING) {
    const header = ['Rank', standings.some((row) => row.players) ? 'Team' : 'Player', ranking.unit || 'Score',
                    'Matches won', 'Matches lost', 'Sets won', 'Sets lost', 'Points scored', 'Points conceded',
                    'Point difference', 'Games', 'Breaks'];
    const rows = standings.map((row) => [
        row.rank, fullName(row), formatScore(row.score, ranking), row.matchesWon, row.matchesLost, row.setsWon, row.setsLost,
        row.pointsFor, row.pointsAgainst, row.pointDiff, row.games, row.breaks,
    ]);
    return '﻿' + [header, ...rows].map((line) => line.map(cell).join(',')).join('\r\n') + '\r\n';
}

export const fileNameFor = (name, extension) =>
    `${String(name || 'tournament').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'tournament'}-results.${extension}`;

const MEDALS = ['#d4a017', '#9aa4b2', '#b87333'];

// A picture of the ranking (PNG), drawn by hand: title, date, and one line per player.
export function drawRanking(canvas, { name, dates, standings, ranking = CLASSIC_RANKING }) {
    const rows = standings.slice(0, 40);
    const width = 900;
    const rowHeight = 46;
    const top = dates ? 150 : 120;
    const height = top + rows.length * rowHeight + 70;
    canvas.width = width;
    canvas.height = height;
    const g = canvas.getContext('2d');
    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, width, height);
    g.fillStyle = '#4f46e5';
    g.fillRect(0, 0, width, 12);
    g.fillStyle = '#151a26';
    g.font = '700 42px system-ui, sans-serif';
    g.fillText(name, 48, 84, width - 96);
    if (dates) {
        g.fillStyle = '#5b6478';
        g.font = '400 24px system-ui, sans-serif';
        g.fillText(dates, 48, 122);
    }
    rows.forEach((row, index) => {
        const y = top + index * rowHeight;
        if (index % 2 === 0) {
            g.fillStyle = '#f4f5fb';
            g.fillRect(32, y - 6, width - 64, rowHeight);
        }
        g.fillStyle = MEDALS[index] && row.rank <= 3 ? MEDALS[index] : '#5b6478';
        g.font = '700 26px system-ui, sans-serif';
        g.fillText(String(row.rank), 52, y + 26);
        g.fillStyle = '#151a26';
        g.font = `${row.rank <= 3 ? 700 : 500} 26px system-ui, sans-serif`;
        g.fillText(fullName(row), 120, y + 26, 520);
        g.textAlign = 'right';
        g.font = '700 26px system-ui, sans-serif';
        g.fillText(formatScore(row.score, ranking), width - 190, y + 26);
        g.fillStyle = '#5b6478';
        g.font = '400 22px system-ui, sans-serif';
        g.fillText(`${row.matchesWon}–${row.matchesLost}`, width - 52, y + 26);
        g.textAlign = 'left';
    });
    g.fillStyle = '#8a93a6';
    g.font = '400 20px system-ui, sans-serif';
    g.fillText(`${ranking.unit || 'Score'} · matches won–lost · Tournamental`, 48, height - 26);
    return canvas;
}

export const canvasToBlob = (canvas) => new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));

export function downloadBlob(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
}
